/**
 * Aplicar un cambio en la copia local, al instante (antes de que llegue a Google). La copia local nunca es la
 * verdad: tras escribir en el libro se vuelve a sincronizar y se reemplaza por lo que diga el libro.
 */
import type { Command } from './write.js';
import type { Workbook } from './parse.js';
import { movementFingerprint, partFingerprint } from './parse.js';
import { mergeBudget, mergeKeywords, renameNotes } from './categories.js';
import type { Rule } from '@pf/domain';

export function applyLocally(wb: Workbook, cmd: Command): Workbook {
  switch (cmd.type) {
    case 'update-movement':
      return { ...wb, movements: wb.movements.map(m => (m.id === cmd.id ? withFp({ ...m, ...cmd.changes }) : m)) };
    case 'add-movement': {
      if (wb.movements.some(m => m.id === cmd.movement.id)) return wb;
      const row = Math.max(1, ...wb.movements.map(m => m.row)) + 1;
      return { ...wb, movements: [...wb.movements, withFp({ ...cmd.movement, row, fingerprint: '' })] };
    }
    case 'delete-movement':
      return { ...wb, movements: wb.movements.filter(m => m.id !== cmd.id), parts: wb.parts.filter(p => p.movementId !== cmd.id) };
    case 'upsert-planned': {
      const rest = (wb.planned ?? []).filter(p => p.id !== cmd.planned.id);
      return { ...wb, planned: [...rest, { ...cmd.planned, row: (wb.planned ?? []).find(p => p.id === cmd.planned.id)?.row ?? 0 }] };
    }
    case 'delete-planned':
      return { ...wb, planned: (wb.planned ?? []).filter(p => p.id !== cmd.id) };
    case 'add-orders': {
      const have = new Set((wb.orders ?? []).map(o => o.ref));
      return { ...wb, orders: [...(wb.orders ?? []), ...cmd.rows.filter(r => !have.has(r.ref)).map(r => ({ ...r, row: 0 }))] };
    }
    case 'link-orders': {
      const refs = new Set(cmd.refs);
      return { ...wb, orders: (wb.orders ?? []).map(o => (refs.has(o.ref) ? { ...o, movementId: cmd.movementId } : o)) };
    }
    case 'compact-movements':
      return wb;   // las filas se recolocan en el libro; la siguiente sincronización trae las nuevas
    case 'save-parts': {
      const others = wb.parts.filter(p => p.movementId !== cmd.movementId);
      const row = Math.max(1, ...wb.parts.map(p => p.row));
      return { ...wb, parts: [...others, ...cmd.parts.map((p, i) => ({ ...p, row: row + i + 1, fingerprint: partFingerprint(p) }))] };
    }
    case 'upsert-budget': {
      const b = cmd.budget;
      const rest = wb.budget.filter(x => !(x.year === b.year && x.category.toLowerCase() === b.category.toLowerCase()));
      return { ...wb, budget: [...rest, b] };
    }
    case 'delete-budget':
      return { ...wb, budget: wb.budget.filter(x => !(x.year === cmd.year && x.category.toLowerCase() === cmd.category.toLowerCase())) };
    case 'add-category':
      return wb.categories.some(c => c.name.toLowerCase() === cmd.category.name.toLowerCase()) ? wb : { ...wb, categories: [...wb.categories, cmd.category] };
    case 'update-category':
      return { ...wb, categories: wb.categories.map(c => (c.name.toLowerCase() === cmd.name.toLowerCase() ? { ...cmd.category, name: c.name, nextMonthFrom: cmd.category.nextMonthFrom !== undefined ? cmd.category.nextMonthFrom : c.nextMonthFrom } : c)) };
    case 'delete-rule': {
      const key = (r: Rule) => JSON.stringify([r.account, r.contains, r.regex, r.amountEquals, r.direction, r.category].map(v => String(v ?? '').toLowerCase()));
      const i = wb.rules.findIndex(r => key(r) === key(cmd.rule));
      return i < 0 ? wb : { ...wb, rules: wb.rules.filter((_, n) => n !== i) };
    }
    case 'rename-category': {
      const is = (n: string, x: string) => n.trim().toLowerCase() === x.trim().toLowerCase();
      const to = cmd.to.trim();
      const target = wb.categories.find(c => is(c.name, to) && !is(c.name, cmd.from));
      const ren = (n: string) => (is(n, cmd.from) ? (target?.name ?? to) : n);
      const gone = wb.categories.find(c => is(c.name, cmd.from));
      const categories = target
        ? wb.categories.filter(c => c !== gone).map(c => (c === target && gone ? { ...c, keywords: mergeKeywords(c.keywords, gone.keywords) } : c))
        : wb.categories.map(c => (c === gone ? { ...c, name: to } : c));
      const budget: typeof wb.budget = [];
      for (const b of wb.budget) {
        if (!is(b.category, cmd.from)) { budget.push(b); continue; }
        const k = wb.budget.find(x => x !== b && is(x.category, ren(b.category)) && x.year === b.year && !is(x.category, cmd.from));
        if (!k) budget.push({ ...b, category: ren(b.category) });
      }
      // Solo se suman en las filas que ya eran de la que queda (las de la que desaparece, si no había, solo cambian de nombre)
      const merged = budget.map(b => { const g = wb.budget.find(x => is(x.category, cmd.from) && x.year === b.year); return target && g && wb.budget.includes(b) && is(b.category, target.name) ? mergeBudget(b, g) : b; });
      return { ...wb, categories, budget: merged,
        movements: wb.movements.map(m => (is(m.category, cmd.from) ? { ...m, category: ren(m.category) } : m)),
        parts: wb.parts.map(p => (is(p.category, cmd.from) ? { ...p, category: ren(p.category) } : p)),
        rules: wb.rules.map(r => (is(r.category, cmd.from) ? { ...r, category: ren(r.category) } : r)),
        planned: wb.planned.map(p => (is(p.category, cmd.from) ? { ...p, category: ren(p.category) } : p)),
        notes: renameNotes(wb.notes, cmd.from, target?.name ?? to) };
    }
    case 'delete-category': {
      const is = (n: string) => n.trim().toLowerCase() === cmd.name.trim().toLowerCase();
      return { ...wb, categories: wb.categories.filter(c => !is(c.name)), budget: wb.budget.filter(b => !is(b.category)),
        notes: wb.notes.filter(n => !is(n.category)), rules: wb.rules.filter(r => !is(r.category)) };
    }
    case 'add-rule':
      return { ...wb, rules: [...wb.rules, cmd.rule] };
    case 'assign-id':
      return { ...wb, movements: wb.movements.map(m => (m.row === cmd.row && !m.id ? withFp({ ...m, id: cmd.id }) : m)) };
    case 'clear-orphan-id':
      return { ...wb, orphanIds: wb.orphanIds.filter(o => o.row !== cmd.row) };
    case 'rewrite-formulas':
      return wb;
    case 'import-batch': {
      const have = new Set(wb.movements.map(m => m.id));
      let row = Math.max(1, ...wb.movements.map(m => m.row));
      const added = cmd.movements.filter(m => !have.has(m.id)).map(m => withFp({ ...m, row: ++row, fingerprint: '' }));
      const metaIds = new Set(wb.meta.map(m => m.id));
      return { ...wb, movements: [...wb.movements, ...added], meta: [...wb.meta, ...cmd.meta.filter(m => !metaIds.has(m.id))] };
    }
    case 'add-notes-tab':
      return { ...wb, missingTabs: wb.missingTabs.filter(t => t !== 'Notas') };
    case 'set-note': {
      const same = (n: { month: string; category: string }) => n.month === cmd.month && n.category.toLowerCase() === cmd.category.toLowerCase();
      const rest = wb.notes.filter(n => !same(n));
      const note = cmd.note.trim();
      return { ...wb, notes: note ? [...rest, { month: cmd.month, category: cmd.category, note, row: wb.notes.find(same)?.row ?? 0 }] : rest };
    }
    case 'add-aliases':
      return { ...wb, aliases: [...wb.aliases, ...cmd.aliases] };
    case 'send-tricount': {
      const has = wb.meta.some(m => m.id === cmd.movementId);
      const sent = { tricountSentAt: cmd.sentAt, tricountAmount: cmd.expense.amount };
      return { ...wb, meta: has ? wb.meta.map(m => (m.id === cmd.movementId ? { ...m, ...sent } : m))
        : [...wb.meta, { id: cmd.movementId, source: 'app', sourceRef: '', valueDate: '', balanceAfter: null, importedAt: '', ...sent }] };
    }
    case 'settle-tricount': case 'record-settlement': case 'link-settlement':
      return wb;   // solo el libro de Tricount
    case 'unsend-tricount':
      if (!cmd.movementId) return wb;
      return { ...wb, movements: wb.movements.map(m => (m.id === cmd.movementId ? { ...m, shared: false } : m)),
        meta: wb.meta.map(m => (m.id === cmd.movementId ? { ...m, tricountSentAt: '', tricountAmount: null } : m)) };
    case 'add-account':
      return wb.accounts.some(a => a.name.toLowerCase() === cmd.account.name.toLowerCase()) ? wb
        : { ...wb, accounts: [...wb.accounts, { ...cmd.account, use: cmd.account.use ?? '', people: cmd.account.people ?? '', split: cmd.account.split ?? '', archived: 0, bankBalance: null, bankBalanceDate: '' }] };
    case 'update-account':
      return { ...wb, accounts: wb.accounts.map(a => (a.name === cmd.name ? { ...a, ...cmd.account, use: cmd.account.use ?? a.use, people: cmd.account.people ?? a.people, split: cmd.account.split ?? a.split } : a)) };
    case 'set-bank-balance':
      return { ...wb, accounts: wb.accounts.map(a => (a.name === cmd.account ? { ...a, bankBalance: cmd.balance, bankBalanceDate: cmd.date } : a)) };
    case 'upsert-snapshot': {
      const i = wb.snapshots.findIndex(x => x.month === cmd.snapshot.month);
      if (i >= 0 && cmd.onlyIfMissing) return wb;
      const row = i >= 0 ? wb.snapshots[i]!.row : Math.max(1, ...wb.snapshots.map(x => x.row)) + 1;
      const next = { ...cmd.snapshot, row };
      return { ...wb, snapshots: i >= 0 ? wb.snapshots.map((x, n) => (n === i ? next : x)) : [...wb.snapshots, next] };
    }
    case 'add-flow':
      return withFund({ ...wb, flows: [...wb.flows, cmd.flow] }, cmd.flow.fund);
    case 'set-price': {
      const w = withFund(wb, cmd.fund);
      return { ...w, positions: w.positions.map(p => (p.fund.toLowerCase() === cmd.fund.toLowerCase() ? { ...p, price: cmd.price, priceDate: cmd.date } : p)) };
    }
  }
}

function withFund(wb: Workbook, fund: string): Workbook {
  return wb.positions.some(p => p.fund.toLowerCase() === fund.toLowerCase()) ? wb : { ...wb, positions: [...wb.positions, { fund, price: null, priceDate: '' }] };
}

function withFp<T extends Parameters<typeof movementFingerprint>[0] & { fingerprint: string }>(m: T): T {
  return { ...m, fingerprint: movementFingerprint(m) };
}
